import { FC, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  useToast,
  Text,
  Icon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Image,
  FormHelperText,
} from '@chakra-ui/react';
import { MdSave, MdArrowBack } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { useForm } from 'react-hook-form';
import { Panel, PageHeading } from '@bemap/ui';
import { createCourse, Course } from '@/services/university-service';

interface CourseFormData {
  title: string;
  description: string;
  instructor: string;
  category: 'partner' | 'analyst';
  level: 'beginner' | 'intermediate' | 'advanced';
  duration: number;
  price: number;
  thumbnail?: File;
  // isActive: boolean;
  // maxStudents: number;
  // tags: string;
  // prerequisites: string;
  // learningObjectives: string;
}

const CreateCoursePage: FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm<CourseFormData>({
    defaultValues: {
      title: '',
      description: '',
      instructor: '',
      category: 'partner',
      level: 'beginner',
      duration: 0,
      price: 0,
    }
  });

  const handleBack = () => {
    navigate(ADMIN_ROUTES.UNIVERSITY_COURSES);
  };

  const onSubmit = async (data: CourseFormData) => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('instructor', data.instructor);
      formData.append('category', data.category);
      formData.append('level', data.level);
      formData.append('durationMinutes', `${data.duration * 60}`);
      formData.append('price', `${data.price}`);
      formData.append('thumbnailUrl', selectedFile as File);

      const response = await createCourse(formData);

      if (response.statusCode === 201 || response.statusCode === 200) {
        toast({
          title: t('university.courses.course_created'),
          description: `${t('university.courses.create_course')}${data.title}" ${t('university.courses.course_created')}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Reset form
        reset({
          title: '',
          description: '',
          instructor: '',
          category: 'partner',
          level: 'beginner',
          duration: 0,
          price: 0,
          thumbnail: undefined
        });
        setThumbnailPreview('');

        // Navigate to the new course
        if (response.message && typeof response.message === 'object' && 'id' in response.message) {
          const newCourse = response.message as Course;
          navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${newCourse.id}`);
        } else {
          navigate(ADMIN_ROUTES.UNIVERSITY_COURSES);
        }
      } else {
        throw new Error(response.message || t('university.courses.error_creating_course'));
      }
    } catch (error: any) {
      console.error('Error creating course:', error);
      toast({
        title: t('university.courses.error_creating_course'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setThumbnailPreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={t('university.courses.create_course')} actions={[
        {
          label: t('university.courses.back_to_courses'),
          onClick: handleBack,
          icon: <Icon as={MdArrowBack} />,
          variant: 'outline'
        }
      ]} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack align="stretch" spacing={6}>
          {/* Basic Information */}
          <Panel heading={t('university.courses.basic_information')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!errors.title} isRequired>
                <FormLabel>{t('university.courses.course_title')}</FormLabel>
                <Input
                  {...register('title', {
                    required: t('university.validation.title_required'),
                    minLength: { value: 3, message: t('university.validation.title_min_length') }
                  })}
                  placeholder={t('university.placeholders.course_title')}
                />
                <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.description} isRequired>
                <FormLabel>{t('university.courses.description')}</FormLabel>
                <Textarea
                  {...register('description', {
                    required: t('university.validation.description_required'),
                    minLength: { value: 10, message: t('university.validation.description_min_length') }
                  })}
                  placeholder={t('university.placeholders.course_description')}
                  rows={4}
                />
                <FormHelperText>Mínimo de 10 caracteres</FormHelperText>
                <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
              </FormControl>

              <HStack spacing={4}>
                <FormControl isInvalid={!!errors.instructor} isRequired>
                  <FormLabel>{t('university.courses.instructor')}</FormLabel>
                  <Input
                    {...register('instructor', {
                      required: t('university.validation.instructor_required')
                    })}
                    placeholder={t('university.placeholders.instructor_name')}
                  />
                  <FormErrorMessage>{errors.instructor?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.category} isRequired>
                  <FormLabel>{t('university.courses.category')}</FormLabel>
                  <Select {...register('category', {
                    required: t('university.validation.category_required')
                  })}>
                    <option value="partner">{t('university.courses.categories.partner')}</option>
                    <option value="analyst">{t('university.courses.categories.analyst')}</option>
                  </Select>
                  <FormErrorMessage>{errors.category?.message}</FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>{t('university.courses.level')}</FormLabel>
                  <Select {...register('level')}>
                    <option value="beginner">{t('university.levels.0.beginner')}</option>
                    <option value="intermediate">{t('university.levels.1.intermediate')}</option>
                    <option value="advanced">{t('university.levels.2.advanced')}</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.courses.duration_hours')}</FormLabel>
                  <NumberInput min={0}>
                    <NumberInputField {...register('duration', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.courses.price')}</FormLabel>
                  <NumberInput min={0}>
                    <NumberInputField {...register('price', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </HStack>
            </VStack>
          </Panel>

          {/* Course Image */}
          <Panel heading={t('university.courses.course_thumbnail')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('university.courses.upload_thumbnail')}</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  p={1}
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  {t('university.courses.recommended_size')}
                </Text>
              </FormControl>

              {thumbnailPreview && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    {t('university.courses.preview')}
                  </Text>
                  <Image
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    maxH="200px"
                    borderRadius="md"
                    objectFit="cover"
                  />
                </Box>
              )}
            </VStack>
          </Panel>

          {/* Additional Details */}
          {/* <Panel heading={t('university.courses.additional_details')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('university.courses.learning_objectives')}</FormLabel>
                <Textarea
                  {...register('learningObjectives')}
                  placeholder={t('university.placeholders.learning_objectives')}
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('university.courses.prerequisites')}</FormLabel>
                <Textarea
                  {...register('prerequisites')}
                  placeholder={t('university.placeholders.prerequisites')}
                  rows={2}
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('university.courses.tags')}</FormLabel>
                <Input
                  {...register('tags')}
                  placeholder={t('university.placeholders.tags_example')}
                />
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>{t('university.courses.max_students')}</FormLabel>
                  <NumberInput min={1} max={1000}>
                    <NumberInputField {...register('maxStudents', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.courses.status')}</FormLabel>
                  <HStack>
                    <Switch {...register('isActive')} colorScheme="blue" />
                    <Text fontSize="sm" color="gray.600">
                      {watch('isActive') ? t('university.courses.status.active') : t('university.courses.status.draft')}
                    </Text>
                  </HStack>
                </FormControl>
              </HStack>
            </VStack>
          </Panel> */}

          {/* Action Buttons */}
          <HStack justify="space-between">
            <Button
              variant="outline"
              onClick={handleBack}
              isDisabled={isSubmitting}
            >
              {t('actions.cancel')}
            </Button>

            <HStack spacing={3}>
              <Button
                type="submit"
                colorScheme="blue"
                leftIcon={<Icon as={MdSave} />}
                isLoading={isSubmitting}
                loadingText={t('general.creating')}
                isDisabled={!isValid}
              >
                {t('university.courses.create_course')}
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </form>
    </VStack>
  );
};

export default CreateCoursePage; 